import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Link2, Paperclip, Trash2, Upload } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import useToast from "../../hooks/useToast";
import useTaskBoardSocket from "../../hooks/useTaskBoardSocket";
import Toast from "../../components/ui/Toast";
import Button from "../../components/forms/Button";
import Input from "../../components/forms/Input";
import BaseModal from "../../components/modals/BaseModal";
import { isAdmin } from "../../utils/permissions";
import { sprintService, apiErrorMessage } from "../../api/taskBoardService";
import { uploadFile, IMAGES_BUCKET, isSupabaseStorageAvailable } from "../../api/supabaseStorage";
import CatalogDetailLayout from "../../components/task-board/CatalogDetailLayout";
import { SprintFormModal } from "../../components/task-board/CatalogFormModals";
import SprintReportLink from "../../components/task-board/SprintReportLink";
import SprintPlanTab from "../../components/task-board/SprintPlanTab";
import SprintTimelineTab from "../../components/task-board/SprintTimelineTab";
import SprintRetrospectiveTab from "../../components/task-board/SprintRetrospectiveTab";
import { SectionCard, StatusPill, daysUntil, formatDate, isSafeUrl } from "../../components/task-board/shared";

const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

const TABS = [
  ["overview", "Overview"],
  ["plan", "Sprint Plan"],
  ["timeline", "Timeline"],
  ["retrospective", "Retrospective"],
];

const Prose = ({ text, empty }) =>
  text ? (
    <p className="text-sm text-gray-800 dark:text-light-200 whitespace-pre-wrap break-words">{text}</p>
  ) : (
    <p className="text-sm italic text-gray-400 dark:text-light-500">{empty}</p>
  );

const formatSize = (bytes) =>
  bytes == null ? "" : bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

const SprintDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { auth } = useAuth();
  const admin = isAdmin(auth?.employee);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.some(([k]) => k === searchParams.get("tab")) ? searchParams.get("tab") : "overview";
  const setTab = (key) =>
    setSearchParams(key === "overview" ? {} : { tab: key }, { replace: true });
  // Bumped (debounced) on any task / sprint change so the tabs re-read the API.
  const [refreshKey, setRefreshKey] = useState(0);
  const bumpTimer = useRef(null);
  const [sprint, setSprint] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  // "Add link" form (e.g. a Google Docs URL): { name, url, error, saving }.
  const [linkForm, setLinkForm] = useState(null);
  const fileInput = useRef(null);
  const { toast, showError, showSuccess, hideToast } = useToast();

  const load = useCallback(async () => {
    try {
      setSprint((await sprintService.get(id)).data);
      setError("");
    } catch (err) {
      setError(apiErrorMessage(err, "Could not load the sprint"));
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const bump = useCallback(() => {
    clearTimeout(bumpTimer.current);
    bumpTimer.current = setTimeout(() => {
      setRefreshKey((k) => k + 1);
      load();
    }, 400);
  }, [load]);
  useEffect(() => () => clearTimeout(bumpTimer.current), []);
  useTaskBoardSocket({
    token: auth?.accessToken || localStorage.getItem("accessToken"),
    onTask: bump,
    onTaskRemoved: bump,
    onCatalogChanged: bump,
    onResync: bump,
  });

  // Optional documents: uploaded to the existing Supabase bucket (same flow as
  // tickets/chat); the API stores only the URL and metadata.
  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > MAX_ATTACHMENT_BYTES) return showError("Files must be 25 MB or smaller");
    setUploading(true);
    try {
      const safeName = file.name.replace(/[^\w.-]+/g, "_");
      const { publicUrl, error: uploadError } = await uploadFile(
        IMAGES_BUCKET,
        `sprints/${id}/${Date.now()}-${safeName}`,
        file,
      );
      if (uploadError || !publicUrl) throw new Error(uploadError?.message || "Upload failed");
      const res = await sprintService.addAttachment(id, {
        name: file.name,
        url: publicUrl,
        contentType: file.type || null,
        size: file.size,
      });
      setSprint((prev) => ({ ...prev, attachments: res.data.attachments }));
      showSuccess("Document attached");
    } catch (err) {
      showError(apiErrorMessage(err, "Could not attach the document"));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  // Links are stored like files (name + URL) but have no size/type.
  const addLink = async () => {
    const name = linkForm.name.trim();
    const url = linkForm.url.trim();
    if (!url || !isSafeUrl(url)) return setLinkForm((f) => ({ ...f, error: "Enter a valid http(s) link" }));
    setLinkForm((f) => ({ ...f, saving: true, error: "" }));
    try {
      const res = await sprintService.addAttachment(id, { name: name || url, url });
      setSprint((prev) => ({ ...prev, attachments: res.data.attachments }));
      setLinkForm(null);
      showSuccess("Link added");
    } catch (err) {
      setLinkForm((f) => ({ ...f, saving: false, error: apiErrorMessage(err, "Could not add the link") }));
    }
  };

  const removeAttachment = async (attachmentId) => {
    try {
      const res = await sprintService.removeAttachment(id, attachmentId);
      setSprint((prev) => ({ ...prev, attachments: res.data.attachments }));
    } catch (err) {
      showError(apiErrorMessage(err, "Could not remove the document"));
    }
  };

  const remaining = sprint && sprint.status !== "completed" ? daysUntil(sprint.endDate) : null;

  return (
    <>
      <CatalogDetailLayout
        backTo="/task-board/sprints"
        backLabel="All sprints"
        title={sprint?.name || "Sprint"}
        titleAddon={sprint && <StatusPill status={sprint.status} />}
        subtitle={
          sprint &&
          `${formatDate(sprint.startDate)} → ${formatDate(sprint.endDate)}${
            remaining != null ? ` · ${remaining >= 0 ? `${remaining} day(s) left` : "ended"}` : ""
          }`
        }
        loading={!sprint && !error}
        error={error}
        onRetry={load}
        admin={admin}
        entityLabel="sprint"
        onEdit={() => setEditing(true)}
        onDelete={async () => {
          await sprintService.remove(id);
          navigate("/task-board/sprints", { replace: true });
        }}
        boardFilter={`sprint=${id}`}
        taskCounts={sprint?.taskCounts}
      >
        {sprint && (
          <div role="tablist" aria-label="Sprint sections" className="flex gap-1 p-1 rounded-xl border border-line2 bg-panel overflow-x-auto">
            {TABS.map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                onClick={() => setTab(key)}
                className={`h-8 px-3 shrink-0 rounded-lg text-[13px] transition-colors ${
                  tab === key ? "bg-accent-soft text-accent-ink font-medium" : "text-muted hover:text-ink hover:bg-hover"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
        {sprint && tab === "plan" && (
          <SprintPlanTab
            sprint={sprint}
            admin={admin}
            refreshKey={refreshKey}
            onSprintChange={(updated) => setSprint((prev) => ({ ...prev, attachments: updated.attachments }))}
            showError={showError}
            showSuccess={showSuccess}
          />
        )}
        {sprint && tab === "timeline" && <SprintTimelineTab
            sprint={sprint}
            admin={admin}
            refreshKey={refreshKey}
            onSprintChange={(updated) => setSprint((prev) => ({ ...prev, attachments: updated.attachments }))}
            showError={showError}
            showSuccess={showSuccess}
          />}
        {sprint && tab === "retrospective" && (
          <SprintRetrospectiveTab
            sprint={sprint}
            admin={admin}
            refreshKey={refreshKey}
            onSprintChange={(updated) => setSprint((prev) => ({ ...prev, attachments: updated.attachments }))}
            onCompleted={load}
            showError={showError}
            showSuccess={showSuccess}
          />
        )}
        {sprint && tab === "overview" && (
          <>
            <SectionCard title="Sprint goal">
              <Prose text={sprint.goal} empty="No goal set" />
            </SectionCard>
            <SectionCard title="Important notes">
              <Prose text={sprint.notes} empty="No notes" />
            </SectionCard>
            <SectionCard
              title="Documents"
              actions={
                admin && (
                  <div className="flex gap-2">
                    <Button variant="secondary" size="sm" onClick={() => setLinkForm({ name: "", url: "", error: "", saving: false })}>
                      <Link2 className="w-4 h-4" /> Add link
                    </Button>
                    {isSupabaseStorageAvailable() && (
                      <>
                        <input ref={fileInput} type="file" className="hidden" onChange={(e) => handleUpload(e.target.files?.[0])} />
                        <Button variant="secondary" size="sm" onClick={() => fileInput.current?.click()} disabled={uploading}>
                          <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Attach"}
                        </Button>
                      </>
                    )}
                  </div>
                )
              }
            >
              <SprintReportLink sprint={sprint} admin={admin} />
              {sprint.attachments?.length ? (
                <ul className="divide-y divide-gray-200 dark:divide-dark-100">
                  {sprint.attachments.map((a) => (
                    <li key={a._id} className="flex items-center gap-3 py-2">
                      {a.size == null ? (
                        <Link2 className="w-4 h-4 text-gray-400 shrink-0" aria-label="Link" />
                      ) : (
                        <Paperclip className="w-4 h-4 text-gray-400 shrink-0" aria-label="File" />
                      )}
                      <a href={a.url} target="_blank" rel="noopener noreferrer" className="flex-1 min-w-0 text-sm text-primary-accent hover:underline truncate">
                        {a.name}
                      </a>
                      <span className="text-xs text-gray-500 dark:text-light-500 shrink-0">
                        {a.size == null ? new URL(a.url).hostname.replace(/^www\./, "") : formatSize(a.size)}
                      </span>
                      {admin && (
                        <button
                          type="button"
                          onClick={() => removeAttachment(a._id)}
                          className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                          aria-label={`Remove ${a.name}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                !(admin && sprint.status === "completed") && (
                  <Prose empty="No documents yet. Attach a file or add a link (e.g. Google Docs)." />
                )
              )}
            </SectionCard>
          </>
        )}
      </CatalogDetailLayout>
      {sprint && (
        <SprintFormModal
          isOpen={editing}
          sprint={sprint}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      )}
      <BaseModal
        isOpen={Boolean(linkForm)}
        onClose={() => setLinkForm(null)}
        title="Add document link"
        subtitle="For example a Google Docs, Sheets or Drive link"
        size="sm"
        footer={
          <div className="flex w-full justify-end gap-2">
            <Button variant="secondary" onClick={() => setLinkForm(null)} disabled={linkForm?.saving}>
              Cancel
            </Button>
            <Button onClick={addLink} disabled={linkForm?.saving}>
              {linkForm?.saving ? "Adding…" : "Add link"}
            </Button>
          </div>
        }
      >
        {linkForm && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              addLink();
            }}
          >
            <Input
              label="Link"
              type="url"
              required
              autoFocus
              placeholder="https://docs.google.com/…"
              value={linkForm.url}
              onChange={(e) => setLinkForm((f) => ({ ...f, url: e.target.value, error: "" }))}
              error={linkForm.error}
            />
            <Input
              label="Title"
              placeholder="Sprint plan (optional — the link is used if empty)"
              value={linkForm.name}
              maxLength={255}
              onChange={(e) => setLinkForm((f) => ({ ...f, name: e.target.value }))}
            />
            <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
          </form>
        )}
      </BaseModal>
      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </>
  );
};

export default SprintDetails;
